import React from "react";
import Imgix, {
  Background,
  Picture,
  Source,
  PublicConfigAPI,
} from "react-imgix";
import classNames from "classnames";
import PropTypes from "prop-types";
import { getShopifyImageFilePath } from "../../utils/image";

// Constants
import {
  IMGIX_BASE_PATH,
  IMGIX_SHOPIFY_BASE_PATH,
  BREAKPOINTS,
  BREAKPOINT_KEYS,
} from "../../utils/constants";

/*
 * Imgix Image components
 *
 * For imgixParams options, see:
 * https://github.com/imgix/react-imgix#imgixparams--object
 *
 * Notes:
 *   - If `width` or `height` are not passed as imgixProps, passing `sizes`
 *     is recommended:  https://github.com/imgix/react-imgix#sizes--string
 *   - For custom sizes values, use the `default` sizes property.
 *   - Note: The Imgix React library now sets `fit: clip` by default, not `fit: crop`:
 *     https://github.com/imgix/react-imgix/releases/tag/v9.0.0
 *     If this is needed, set it in imgixParams:
 *   - To apply an image className when using `<ImgixPicture>`, add it to the
 *     fallback image, and it will apply at all screen sizes.
 */

// Disable Imgix picture element fallback <img> warning since it's
// firing even when there's a fallback
// See: https://github.com/imgix/react-imgix#warnings
PublicConfigAPI.disableWarning("fallbackImage");

const defaultImgixParams = { auto: "format" };

export const getImageSrc = (src, isShopify) => {
  let imgSrc = `${IMGIX_BASE_PATH}/${src}`;

  if (isShopify) {
    const filePath = getShopifyImageFilePath(src);
    imgSrc = `${IMGIX_SHOPIFY_BASE_PATH}/${filePath}`;
  }

  return imgSrc;
};

export const constructSizesString = (sizes) => {
  if (!sizes) {
    return null;
  }

  const wideMobileSize = sizes.wideMobile
    ? `${BREAKPOINTS[BREAKPOINT_KEYS.WIDE_MOBILE]} ${sizes.wideMobile}`
    : "";
  const tabletSize = sizes.tablet
    ? `${BREAKPOINTS[BREAKPOINT_KEYS.TABLET]} ${sizes.tablet}`
    : "";
  const desktopSize = sizes.desktop
    ? `${BREAKPOINTS[BREAKPOINT_KEYS.DESKTOP]} ${sizes.desktop}`
    : "";
  const defaultSize = sizes.default || "";

  // Enforce ordering of sizes since first match wins:
  // https://developer.mozilla.org/en-US/docs/Learn/HTML/Multimedia_and_embedding/Responsive_images
  return [desktopSize, tabletSize, wideMobileSize, defaultSize]
    .filter(Boolean)
    .join(", ");
};

export const ImgixImage = ({
  src,
  alt,
  className,
  sizes,
  width,
  height,
  imgixProps,
  htmlAttributes,
  isShopify,
}) => {
  const classes = classNames("image", className);
  const imgixParams = { ...defaultImgixParams, ...imgixProps };
  const defaultAttributes = { alt, ...htmlAttributes };
  const imageSrc = getImageSrc(src, isShopify);

  return (
    <Imgix
      className={classes}
      src={imageSrc}
      sizes={constructSizesString(sizes)}
      width={width}
      height={height}
      imgixParams={imgixParams}
      htmlAttributes={defaultAttributes}
    />
  );
};

ImgixImage.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  className: PropTypes.any,
  width: PropTypes.number,
  height: PropTypes.number,
  imgixProps: PropTypes.object,
  htmlAttributes: PropTypes.object,
  sizes: PropTypes.shape({
    desktop: PropTypes.string,
    tablet: PropTypes.string,
    wideMobile: PropTypes.string,
    default: PropTypes.string,
  }),
  isShopify: PropTypes.bool,
};

ImgixImage.defaultProps = {
  isShopify: false,
};

export const ImgixBackgroundImage = ({
  src,
  children,
  className,
  imgixProps,
  htmlAttributes,
  isShopify,
}) => {
  const classes = classNames("image", className);
  const imgixParams = { ...defaultImgixParams, ...imgixProps };
  const imageSrc = getImageSrc(src, isShopify);

  return (
    <Background
      className={classes}
      src={imageSrc}
      imgixParams={imgixParams}
      htmlAttributes={htmlAttributes}
    >
      {children}
    </Background>
  );
};

ImgixBackgroundImage.propTypes = {
  src: PropTypes.string.isRequired,
  children: PropTypes.any.isRequired,
  className: PropTypes.any,
  imgixProps: PropTypes.object,
  htmlAttributes: PropTypes.object,
  isShopify: PropTypes.bool,
};

ImgixBackgroundImage.defaultProps = {
  isShopify: false,
};

export const ImgixSource = ({
  src,
  sizes,
  width,
  height,
  imgixProps,
  htmlAttributes,
  isShopify,
}) => {
  const imgixParams = { ...defaultImgixParams, ...imgixProps };
  const imageSrc = getImageSrc(src, isShopify);

  return (
    <Source
      src={imageSrc}
      sizes={constructSizesString(sizes)}
      width={width}
      height={height}
      imgixParams={imgixParams}
      htmlAttributes={htmlAttributes}
    />
  );
};

ImgixSource.propTypes = {
  src: PropTypes.string.isRequired,
  width: PropTypes.number,
  height: PropTypes.number,
  htmlAttributes: PropTypes.object,
  imgixProps: PropTypes.object,
  sizes: PropTypes.shape({
    desktop: PropTypes.string,
    tablet: PropTypes.string,
    wideMobile: PropTypes.string,
    default: PropTypes.string,
  }),
  isShopify: PropTypes.bool,
};

ImgixSource.defaultProps = {
  isShopify: false,
};

export const ImgixPicture = ({ fallback, children, htmlAttributes }) => {
  return (
    <Picture htmlAttributes={htmlAttributes}>
      {children}
      {fallback}
    </Picture>
  );
};

ImgixPicture.propTypes = {
  fallback: PropTypes.element.isRequired,
  children: PropTypes.any,
  htmlAttributes: PropTypes.object,
};

const ImgixImageSet = {
  ImgixImage,
  ImgixBackgroundImage,
  ImgixSource,
  ImgixPicture,
};

export default ImgixImageSet;
